import { useEffect, useRef, useState } from 'react';

import { applyMetaFilters, parseMetaFilters, EMPTY_META } from './serialization';
import type { MetaFilterItem } from './serialization';
import { useEventListFilters } from '../../../useEventListFilters';

interface UseMetaFiltersReturn {
  rows: MetaFilterItem[];
  updateRow: (index: number, patch: Partial<MetaFilterItem>) => void;
  addRow: () => void;
  removeRow: (index: number) => void;
  clear: () => void;
}

const isDraft = (row: MetaFilterItem): boolean => !(row.key && row.value);

export function useMetaFilters(): UseMetaFiltersReturn {
  const { filters, setFilters } = useEventListFilters();
  const [rows, setRows] = useState<MetaFilterItem[]>(() => parseMetaFilters(filters));
  const appliedRef = useRef(filters);

  useEffect(() => {
    if (filters === appliedRef.current) return;

    appliedRef.current = filters;
    setRows((prev) => [...parseMetaFilters(filters), ...prev.filter(isDraft)]);
  }, [filters]);

  const commit = (nextRows: MetaFilterItem[]) => {
    const nextFilters = applyMetaFilters(filters, nextRows);
    appliedRef.current = nextFilters;
    setRows(nextRows);
    setFilters(nextFilters);
  };

  const updateRow = (index: number, patch: Partial<MetaFilterItem>) => {
    const current = rows[index];
    if (!current) return;

    commit(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addRow = () => setRows([...rows, { ...EMPTY_META }]);

  const removeRow = (index: number) => commit(rows.filter((_, i) => i !== index));

  const clear = () => commit([]);

  return {
    rows, updateRow, addRow, removeRow, clear,
  };
}
